// Voskio: small, dependency-free enhancements. Every page works without this file.
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Scroll reveals: .vk-reveal elements fade up once as they enter the viewport.
  const revealAll = () => document.querySelectorAll('.vk-reveal:not(.is-in)').forEach((el) => el.classList.add('is-in'));
  const initReveals = () => {
    const items = document.querySelectorAll('.vk-reveal:not(.is-in)');
    if (!items.length) return;
    if (reduceMotion.matches || !('IntersectionObserver' in window)) return revealAll();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 },
    );
    items.forEach((el) => observer.observe(el));
  };

  // The drawn hero watch tells the visitor's real time. The seconds hand sweeps in small beats,
  // but only while the watch is on screen and the tab is visible. Reduced motion: hands are set once.
  const setHands = (watch) => {
    const now = new Date();
    const s = now.getSeconds() + now.getMilliseconds() / 1000;
    const m = now.getMinutes() + s / 60;
    const h = (now.getHours() % 12) + m / 60;
    watch.querySelector('.vk-hand--hour')?.setAttribute('transform', `rotate(${h * 30})`);
    watch.querySelector('.vk-hand--minute')?.setAttribute('transform', `rotate(${m * 6})`);
    watch.querySelector('.vk-hand--second')?.setAttribute('transform', `rotate(${s * 6})`);
  };
  const watchesOnScreen = new Set();
  let frame = 0;
  // Four beats a second, like an automatic: smooth to the eye, light on a phone (every frame was too costly).
  const schedule = () => setTimeout(() => requestAnimationFrame(tick), 250);
  const tick = () => {
    watchesOnScreen.forEach(setHands);
    frame = watchesOnScreen.size && document.visibilityState === 'visible' ? schedule() : 0;
  };
  const initWatches = () => {
    const watches = document.querySelectorAll('.vk-watch');
    watches.forEach(setHands);
    if (!watches.length || reduceMotion.matches || !('IntersectionObserver' in window)) return;
    // The hands start moving once the page has loaded, so they never compete with it.
    const start = () => {
      const observer = new IntersectionObserver((entries) => {
        for (const { target, isIntersecting } of entries) {
          if (isIntersecting && !target.dataset.vkSpinning) watchesOnScreen.add(target);
          else watchesOnScreen.delete(target);
        }
        if (watchesOnScreen.size && !frame) frame = schedule();
      });
      watches.forEach((watch) => observer.observe(watch));
    };
    if (document.readyState === 'complete') start();
    else window.addEventListener('load', start, { once: true });
  };

  // 404: the watch has lost track of time. Hands spin backwards through twelve hours, then settle on now.
  const initLostWatch = () => {
    const holder = document.querySelector('[data-vk-lost]');
    const watch = holder?.querySelector('.vk-watch');
    if (!watch || reduceMotion.matches) return;
    watch.dataset.vkSpinning = '1';
    watchesOnScreen.delete(watch);
    const duration = 2800;
    const start = performance.now();
    const ease = (t) => 1 - Math.pow(1 - t, 4);
    const spin = (time) => {
      const t = Math.min(1, (time - start) / duration);
      const shown = new Date(Date.now() + 12 * 3600 * 1000 * (1 - ease(t)));
      const s = shown.getSeconds() + shown.getMilliseconds() / 1000;
      const m = shown.getMinutes() + s / 60;
      const h = (shown.getHours() % 12) + m / 60;
      watch.querySelector('.vk-hand--hour')?.setAttribute('transform', `rotate(${h * 30})`);
      watch.querySelector('.vk-hand--minute')?.setAttribute('transform', `rotate(${m * 6})`);
      watch.querySelector('.vk-hand--second')?.setAttribute('transform', `rotate(${s * 6})`);
      if (t < 1) requestAnimationFrame(spin);
      else {
        delete watch.dataset.vkSpinning;
        watchesOnScreen.add(watch);
        if (!frame) frame = schedule();
      }
    };
    requestAnimationFrame(spin);
  };

  // Pointer tilt with a moving glare, like turning a watch under a lamp. Mouse only; never on touch.
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const initTilt = () => {
    if (reduceMotion.matches || !finePointer.matches) return;
    const selector = '.vk-tilt, .product-card';
    document.querySelectorAll(selector).forEach((el) => {
      if (el.dataset.vkTilt) return;
      el.dataset.vkTilt = '1';
      el.classList.add('vk-tilting');
      let raf = 0;
      el.addEventListener('pointermove', (event) => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const r = el.getBoundingClientRect();
          const x = (event.clientX - r.left) / r.width;
          const y = (event.clientY - r.top) / r.height;
          el.style.setProperty('--ry', `${(x - 0.5) * 8}deg`);
          el.style.setProperty('--rx', `${(0.5 - y) * 8}deg`);
          el.style.setProperty('--gx', `${x * 100}%`);
          el.style.setProperty('--gy', `${y * 100}%`);
        });
      });
      el.addEventListener('pointerleave', () => {
        cancelAnimationFrame(raf);
        ['--rx', '--ry', '--gx', '--gy'].forEach((p) => el.style.removeProperty(p));
      });
    });
  };

  // Sliding banner: native scroll-snap track, so swipe works everywhere; JS adds arrows, dots and autoplay.
  const initSliders = () => {
    document.querySelectorAll('[data-vk-slider]:not([data-ready])').forEach((slider) => {
      slider.dataset.ready = '1';
      const track = slider.querySelector('[data-vk-slider-track]');
      const slides = [...slider.querySelectorAll('[data-vk-slide]')];
      const dots = [...slider.querySelectorAll('[data-vk-slider-dot]')];
      const pauseButton = slider.querySelector('[data-vk-slider-pause]');
      if (!track || slides.length < 2) return;
      const interval = Number(slider.dataset.interval) || 6000;
      slider.style.setProperty('--vk-interval', `${interval}ms`);
      let index = 0;
      let timer = 0;
      let userPaused = reduceMotion.matches;
      let hovered = false;
      let onScreen = false;

      const setActive = (i) => {
        index = i;
        slides.forEach((slide, n) => {
          const active = n === i;
          slide.classList.toggle('is-active', active);
          slide.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', active ? '0' : '-1'));
        });
        dots.forEach((dot, n) => {
          dot.classList.toggle('is-active', n === i);
          if (n === i) dot.setAttribute('aria-current', 'true');
          else dot.removeAttribute('aria-current');
        });
        // Restart the dot's progress fill.
        slider.classList.remove('is-ticking');
        void slider.offsetWidth;
        if (timer) slider.classList.add('is-ticking');
      };
      const goTo = (i) => {
        const n = (i + slides.length) % slides.length;
        track.scrollTo({ left: slides[n].offsetLeft - track.offsetLeft, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        setActive(n);
      };
      const stop = () => {
        clearInterval(timer);
        timer = 0;
        slider.classList.remove('is-ticking');
      };
      const sync = () => {
        const shouldRun = !userPaused && !hovered && onScreen && document.visibilityState === 'visible';
        if (shouldRun && !timer) {
          timer = setInterval(() => goTo(index + 1), interval);
          slider.classList.add('is-ticking');
        } else if (!shouldRun && timer) stop();
        if (pauseButton) {
          pauseButton.classList.toggle('is-paused', userPaused);
          pauseButton.setAttribute('aria-label', userPaused ? pauseButton.dataset.labelPlay : pauseButton.dataset.labelPause);
        }
      };

      slider.querySelector('[data-vk-slider-prev]')?.addEventListener('click', () => { stop(); goTo(index - 1); sync(); });
      slider.querySelector('[data-vk-slider-next]')?.addEventListener('click', () => { stop(); goTo(index + 1); sync(); });
      dots.forEach((dot, n) => dot.addEventListener('click', () => { stop(); goTo(n); sync(); }));
      pauseButton?.addEventListener('click', () => { userPaused = !userPaused; sync(); });
      slider.addEventListener('pointerenter', () => { hovered = true; sync(); });
      slider.addEventListener('pointerleave', () => { hovered = false; sync(); });
      slider.addEventListener('focusin', () => { hovered = true; sync(); });
      slider.addEventListener('focusout', () => { hovered = false; sync(); });
      document.addEventListener('visibilitychange', sync);

      // Swipes and keyboard scrolling update the active slide.
      const slideObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) if (entry.isIntersecting) setActive(slides.indexOf(entry.target));
        },
        { root: track, threshold: 0.6 },
      );
      slides.forEach((slide) => slideObserver.observe(slide));
      new IntersectionObserver(([entry]) => { onScreen = entry.isIntersecting; sync(); }, { threshold: 0.3 }).observe(slider);
      setActive(0);
    });
  };

  // Hero background slideshow: photos crossfade behind the hero (slow zoom on each). Pause button,
  // pauses off screen and when the tab is hidden; never rotates for reduced motion.
  const initHeroBackgrounds = () => {
    document.querySelectorAll('[data-vk-hero-bgs]:not([data-ready])').forEach((wrap) => {
      wrap.dataset.ready = '1';
      const hero = wrap.closest('.vk-hero');
      const images = [...wrap.querySelectorAll('.vk-hero__bg')];
      const dots = [...(hero?.querySelectorAll('.vk-hero__bg-dot') || [])];
      const pauseButton = hero?.querySelector('[data-vk-hero-pause]');
      if (images.length < 2) return;
      const interval = Number(wrap.dataset.interval) || 6000;
      let index = 0;
      let timer = 0;
      let userPaused = reduceMotion.matches;
      let onScreen = true;
      const show = (i) => {
        index = (i + images.length) % images.length;
        const next = images[index];
        next.loading = 'eager';
        images.forEach((img, n) => img.classList.toggle('is-active', n === index));
        dots.forEach((dot, n) => dot.classList.toggle('is-active', n === index));
      };
      const sync = () => {
        const run = !userPaused && onScreen && document.visibilityState === 'visible';
        if (run && !timer) timer = setInterval(() => show(index + 1), interval);
        if (!run && timer) {
          clearInterval(timer);
          timer = 0;
        }
        if (pauseButton) {
          pauseButton.classList.toggle('is-paused', userPaused);
          pauseButton.setAttribute('aria-label', userPaused ? pauseButton.dataset.labelPlay : pauseButton.dataset.labelPause);
        }
      };
      // Warm the next photo so the first crossfade never shows an empty frame.
      images[1].loading = 'eager';
      pauseButton?.addEventListener('click', () => { userPaused = !userPaused; sync(); });
      document.addEventListener('visibilitychange', sync);
      new IntersectionObserver(([entry]) => { onScreen = entry.isIntersecting; sync(); }).observe(wrap);
      sync();
    });
  };

  // Scroll wipe: the banner sticks while you scroll (on phones it scrolls with the page); each next photo
  // is uncovered over the last, top to bottom, exactly as far as you have scrolled. Reduced motion keeps the plain stack.
  const initScrollReveal = () => {
    const sections = [...document.querySelectorAll('[data-vk-scroll-reveal]:not([data-ready])')];
    if (!sections.length || reduceMotion.matches) return;
    let queued = false;
    const clamp = (v) => Math.min(1, Math.max(0, v));
    const update = () => {
      queued = false;
      sections.forEach((section) => {
        const layers = section.querySelectorAll('[data-vk-layer]');
        const rect = section.getBoundingClientRect();
        // Pinned (desktop): progress over the section's extra scroll height. Unpinned (phones): over the
        // banner's trip from fully on screen at the bottom to near the top, so nothing waits on empty space.
        const pinned = getComputedStyle(section.querySelector('.vk-reveal-scroll__sticky')).position === 'sticky';
        const start = pinned ? 0 : window.innerHeight - rect.height;
        const travel = Math.max(1, pinned ? rect.height - window.innerHeight : start - window.innerHeight * 0.12);
        const progress = clamp((start - rect.top) / travel) * (layers.length - 1);
        const current = Math.min(layers.length - 1, Math.floor(progress + 0.5));
        layers.forEach((layer, i) => {
          layer.style.setProperty('--vk-reveal', i === 0 ? '1' : clamp(progress - (i - 1)).toFixed(4));
          layer.classList.toggle('is-current', i === current);
        });
        section.querySelectorAll('.vk-reveal-scroll__progress span').forEach((dot, i) => dot.classList.toggle('is-current', i === current));
      });
    };
    const queue = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(update);
    };
    sections.forEach((section) => {
      section.dataset.ready = '1';
      section.classList.add('is-live');
    });
    // Capture: on desktop Horizon scrolls .page-wrapper, not the window.
    document.addEventListener('scroll', queue, { capture: true, passive: true });
    window.addEventListener('resize', queue);
    update();
  };

  // Product gallery: previous/next arrows either side of the thumbnail row, driving Horizon's slideshow.
  const initThumbArrows = () => {
    document.querySelectorAll('template[data-vk-thumb-arrows]:not([data-ready])').forEach((template) => {
      const controls = template.parentElement?.querySelector('slideshow-controls[thumbnails]');
      const slideshow = controls?.closest('slideshow-component');
      if (!slideshow) return;
      template.dataset.ready = '1';
      const arrows = template.content.cloneNode(true);
      const prev = arrows.querySelector('[data-vk-thumb-prev]');
      const next = arrows.querySelector('[data-vk-thumb-next]');
      controls.prepend(prev);
      controls.append(next);
      controls.classList.add('vk-thumbs');
      prev.addEventListener('click', (event) => slideshow.previous(event));
      next.addEventListener('click', (event) => slideshow.next(event));
    });
  };

  // Horizontal strips (Shop by brand): arrows, mouse drag, and arrows that dim at the ends.
  const initStrips = () => {
    document.querySelectorAll('[data-vk-strip]:not([data-ready])').forEach((strip) => {
      strip.dataset.ready = '1';
      const track = strip.querySelector('[data-vk-strip-track]');
      const prev = strip.querySelector('[data-vk-strip-prev]');
      const next = strip.querySelector('[data-vk-strip-next]');
      if (!track) return;
      const step = () => Math.max(240, track.clientWidth * 0.8);
      const behavior = reduceMotion.matches ? 'auto' : 'smooth';
      prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior }));
      next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior }));
      const update = () => {
        const max = track.scrollWidth - track.clientWidth - 2;
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max;
        strip.classList.toggle('is-scrollable', max > 0);
      };
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();

      // Drag to scroll with a mouse (touch already scrolls natively).
      let startX = 0;
      let startLeft = 0;
      let dragged = false;
      track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        startX = event.clientX;
        startLeft = track.scrollLeft;
        dragged = false;
        track.classList.add('is-dragging');
        const move = (e) => {
          const dx = e.clientX - startX;
          if (Math.abs(dx) > 5) dragged = true;
          track.scrollLeft = startLeft - dx;
        };
        const up = () => {
          track.classList.remove('is-dragging');
          window.removeEventListener('pointermove', move);
          window.removeEventListener('pointerup', up);
        };
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', up);
      });
      // A drag must not also follow the link under the pointer.
      track.addEventListener('click', (event) => { if (dragged) { event.preventDefault(); dragged = false; } }, true);
    });
  };

  // Card actions: one delegated listener, so cards loaded later (filters, infinite scroll) work too.
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-vk-action]');
    if (!button || button.tagName === 'A') return;
    event.preventDefault();
    event.stopPropagation();
    const card = button.closest('product-card, .product-card');
    const quickAdd = card?.querySelector('quick-add-component');
    const goToProduct = () => {
      const url = button.dataset.url || card?.querySelector('a[href*="/products/"]')?.getAttribute('href');
      if (url) window.location.href = url;
    };
    switch (button.dataset.vkAction) {
      case 'add': {
        const mode = quickAdd?.dataset.quickAddButton;
        const target = quickAdd?.querySelector(`.quick-add__button--${mode}`) || quickAdd?.querySelector('.quick-add__button');
        if (target) target.click();
        else goToProduct();
        break;
      }
      case 'view':
        if (quickAdd && typeof quickAdd.handleClick === 'function') quickAdd.handleClick(new Event('click', { cancelable: true }));
        else goToProduct();
        break;
    }
  }, true);

  // Video cards: play/pause and sound buttons. A video the shopper paused stays paused.
  const initVideoControls = () => {
    document.querySelectorAll('.vk-video-card:not([data-ready])').forEach((card) => {
      card.dataset.ready = '1';
      const video = card.querySelector('video');
      const toggle = card.querySelector('[data-vk-video-toggle]');
      const sound = card.querySelector('[data-vk-video-sound]');
      if (!video) return;
      const syncButtons = () => {
        toggle?.classList.toggle('is-paused', video.paused);
        toggle?.setAttribute('aria-label', video.paused ? toggle.dataset.labelPlay : toggle.dataset.labelPause);
        sound?.classList.toggle('is-on', !video.muted);
        sound?.setAttribute('aria-label', video.muted ? sound.dataset.labelUnmute : sound.dataset.labelMute);
      };
      video.addEventListener('play', syncButtons);
      video.addEventListener('pause', syncButtons);
      video.addEventListener('volumechange', syncButtons);
      toggle?.addEventListener('click', () => {
        if (video.paused) {
          delete video.dataset.userPaused;
          video.play().catch(() => {});
        } else {
          video.dataset.userPaused = '1';
          video.pause();
        }
      });
      sound?.addEventListener('click', () => {
        const turnOn = video.muted;
        document.querySelectorAll('.vk-video-card video').forEach((other) => { other.muted = true; });
        video.muted = !turnOn;
        if (turnOn && video.paused) video.play().catch(() => {});
      });
      syncButtons();
    });
  };

  // The floating WhatsApp button waits until the first section (the hero has its own WhatsApp button) is out of view.
  const initFloat = () => {
    const float = document.querySelector('.vk-wa-float');
    const first = document.querySelector('.vk-hero') || document.querySelector('#MainContent > :first-child');
    if (!float) return;
    if (!first || !('IntersectionObserver' in window)) return float.classList.add('is-visible');
    new IntersectionObserver(([entry]) => float.classList.toggle('is-visible', !entry.isIntersecting)).observe(first);
  };

  // Videos never preload; each plays muted only while it is on screen.
  const initVideos = () => {
    const videos = document.querySelectorAll('video[data-vk-autoplay]');
    if (!videos.length || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const { target, isIntersecting } of entries) {
          if (isIntersecting && !reduceMotion.matches && !target.dataset.userPaused) target.play().catch(() => {});
          else target.pause();
        }
      },
      { threshold: 0.4 },
    );
    videos.forEach((video) => {
      video.muted = true;
      observer.observe(video);
    });
  };

  const init = () => {
    initReveals();
    initWatches();
    initLostWatch();
    initTilt();
    initFloat();
    initVideos();
    initVideoControls();
    initSliders();
    initHeroBackgrounds();
    initScrollReveal();
    initStrips();
    initThumbArrows();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Theme editor: re-run when a section is added or changed.
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && watchesOnScreen.size && !frame) frame = schedule();
  });
})();
